import { apiService } from './api';
import { 
  QuestionnaireSession, 
  QuestionnaireResponse, 
  AIQuestionnaireRequest,
  AIQuestionnaireResponse,
  AIQuestionnaireResponseV2
} from '../types/questionnaire';
// Import the new types we created
import { ScopeAndMaterialPayload } from '../types/scopeAndMaterial';

class QuestionnaireService {
  async startQuestionnaire(projectId: string, useV2: boolean = false): Promise<QuestionnaireSession> {
    return apiService.post<QuestionnaireSession>(`/projects/${projectId}/questionnaire/start`, { useV2 });
  }

  async getQuestionnaireSession(projectId: string): Promise<QuestionnaireSession | null> {
    try {
      return await apiService.get<QuestionnaireSession>(`/projects/${projectId}/questionnaire`);
    } catch (error: any) {
      // Return null for 404 (no session found) instead of throwing
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getNextQuestion(projectId: string, sessionId: string): Promise<AIQuestionnaireResponse> {
    return apiService.post<AIQuestionnaireResponse>(`/projects/${projectId}/questionnaire/${sessionId}/next`, {});
  }

  async getNextQuestionV2(projectId: string, sessionId: string): Promise<AIQuestionnaireResponseV2> {
    return apiService.post<AIQuestionnaireResponseV2>(`/projects/${projectId}/questionnaire/${sessionId}/next-v2`, {});
  }

  async submitResponse(
    projectId: string, 
    sessionId: string, 
    response: Omit<QuestionnaireResponse, 'timestamp'>
  ): Promise<QuestionnaireSession> {
    return apiService.post<QuestionnaireSession>(
      `/projects/${projectId}/questionnaire/${sessionId}/response`, 
      response
    );
  }

  async updateResponse(
    projectId: string, 
    sessionId: string, 
    questionId: string,
    response: Omit<QuestionnaireResponse, 'timestamp'>
  ): Promise<QuestionnaireSession> {
    return apiService.put<QuestionnaireSession>(
      `/projects/${projectId}/questionnaire/${sessionId}/response/${questionId}`, 
      response
    );
  }

  async completeQuestionnaire(projectId: string, sessionId: string): Promise<QuestionnaireSession> {
    return apiService.post<QuestionnaireSession>(`/projects/${projectId}/questionnaire/${sessionId}/complete`, {});
  }

  async getQuestionnaireResponses(projectId: string): Promise<QuestionnaireResponse[]> {
    return apiService.get<QuestionnaireResponse[]>(`/projects/${projectId}/questionnaire/responses`);
  }

  async getBasicQuestions(projectId: string, projectType: string): Promise<{
    questions: any[];
    existingResponses?: Record<string, any>;
  }> {
    return apiService.get<{ questions: any[]; existingResponses?: Record<string, any> }>(
      `/projects/${projectId}/questionnaire/basic-questions?projectType=${projectType}`
    );
  }

  async saveBasicQuestions(projectId: string, data: { questionsWithAnswers: any[] }): Promise<{ sessionId: string }> {
    return apiService.post<{ sessionId: string }>(`/projects/${projectId}/questionnaire/basic-questions`, data);
  }

  async generateWorkflowStream(projectId: string, sessionId: string): Promise<{
    success: boolean;
    workflow?: any;
    status: string;
    message?: string;
    sessionId?: string;
  }> {
    return apiService.post<{ success: boolean; workflow?: any; status: string; message?: string; sessionId?: string }>(
      `/projects/${projectId}/questionnaire/generate-workflow-stream`,
      { sessionId }
    );
  }

  async checkWorkflowStatus(projectId: string): Promise<{
    status: string;
    workflow: any;
    error: string | null;
    sessionId: string;
  }> {
    return apiService.get<{ status: string; workflow: any; error: string | null; sessionId: string }>(
      `/projects/${projectId}/questionnaire/workflow-status`
    );
  }

  async startPhaseQuestionGeneration(
    projectId: string,
    phaseNumber: number,
    phaseData: any
  ): Promise<{
    success: boolean;
    status: string;
    message: string;
    phaseNumber: number;
  }> {
    return apiService.post<{
      success: boolean;
      status: string;
      message: string;
      phaseNumber: number;
    }>(
      `/projects/${projectId}/questionnaire/phase/${phaseNumber}/questions/generate`,
      { phaseData }
    );
  }

  async checkPhaseQuestionsStatus(
    projectId: string,
    phaseNumber: number
  ): Promise<{
    status: string;
    phaseNumber: number;
    questions?: any[];
    technical_specifications?: any[];
    estimated_completion_time?: string;
    phase_name?: string;
    generatedAt?: string;
    error?: string | null;
    savedAnswers?: Record<string, any>;
  }> {
    return apiService.get<{
      status: string;
      phaseNumber: number;
      questions?: any[];
      technical_specifications?: any[];
      estimated_completion_time?: string;
      phase_name?: string;
      generatedAt?: string;
      error?: string | null;
      savedAnswers?: Record<string, any>;
    }>(
      `/projects/${projectId}/questionnaire/phase/${phaseNumber}/questions/status`
    );
  }

  async savePhaseAnswers(projectId: string, phaseNumber: number, answers: Record<string, any>): Promise<{
    success: boolean;
  }> {
    return apiService.post<{ success: boolean }>(
      `/projects/${projectId}/questionnaire/phase/${phaseNumber}/answers`,
      { answers }
    );
  }

  // --- NEW METHODS FOR SCOPE & MATERIALS ---

  /**
   * Fetches the aggregated Master State and Material Log
   */
  async getScopeAndMaterials(projectId: string): Promise<ScopeAndMaterialPayload> {
    console.log(`[Frontend Service] Requesting Scope & Materials for Project: ${projectId}`);
    try {
        const response = await apiService.get<ScopeAndMaterialPayload>(
            `/projects/${projectId}/scope-and-materials`
        );
        console.log(`[Frontend Service] Received Data:`, response);
        return response;
    } catch (error) {
        console.error(`[Frontend Service] Error fetching scope:`, error);
        throw error;
    }
  }

  /**
   * Saves the user's edits to Scope and Materials
   */
  async saveScopeAndMaterials(projectId: string, data: Partial<ScopeAndMaterialPayload>): Promise<{ success: boolean }> {
    return apiService.post<{ success: boolean }>(
      `/projects/${projectId}/scope-and-materials`,
      data
    );
  }
}

export const questionnaireService = new QuestionnaireService();